import type { CalendarAvailability } from '@/components/calendar';
import { isSlotFull } from '@/components/calendar/calendar-utils';
import { listCoveringClassPath, OFFERING_LINKS } from '@/helpers/offering';
import {
  classWithInstructorText,
  formatClassDateTime,
  formatClockTime,
  formatDateInTimeZone,
  formatDateWithConditionalYear,
  getAbbreviatedName,
  type TimeFormat,
} from '@/lib/utils';
import type { WaitlistConfig } from '@/state/tenant';
import type {
  CreditLostReason,
  LiveWaitlistEntry,
  WaitlistCreditOptions,
  WaitlistEntry,
  WaitlistHoldSource,
} from '@/services/waitlist';

export type WaitlistGateReason = 'NOT_FULL' | 'DISABLED' | 'CUTOFF' | 'FULL';

const cutoffAtOf = (
  startTime: string | number | Date,
  cutoffHours: number
): number => new Date(startTime).getTime() - cutoffHours * 60 * 60 * 1000;

// The single joinability predicate: null means she can join, a reason says
// why not. The server re-validates at join with typed errors, so a stale count
// or a skewed clock only mis-shows the CTA.
export const getWaitlistGate = ({
  startTime,
  isFullyBooked,
  waitlistEnabled,
  waitlistCount,
  config,
}: {
  startTime: string | number | Date;
  isFullyBooked: boolean | undefined;
  waitlistEnabled: boolean | undefined;
  waitlistCount: number | undefined;
  config: WaitlistConfig | null | undefined;
}): WaitlistGateReason | null => {
  if (!isFullyBooked) return 'NOT_FULL';
  if (!config?.enabled || !waitlistEnabled) return 'DISABLED';
  if (Date.now() >= cutoffAtOf(startTime, config.cutoff_hours)) return 'CUTOFF';
  if ((waitlistCount ?? 0) >= config.size) return 'FULL';
  return null;
};

export type WaitlistAction = 'join' | 'view';

export const WAITLIST_ACTION_LABEL: Record<WaitlistAction, string> = {
  join: 'Join waitlist',
  view: 'View waitlist',
};

// What a full class time offers her: her existing place wins over a new join,
// so a place stays reachable after the list fills or the cut-off passes.
export const getWaitlistAction = (
  canJoin: boolean,
  entry: LiveWaitlistEntry | undefined
): WaitlistAction | null => {
  if (entry) return 'view';
  return canJoin ? 'join' : null;
};

export type FullCardBadge = {
  label: string;
  color: 'green' | 'yellow' | 'gray';
};

// Her booking or her place wins. Past those the badge belongs to the
// waitlist, so with it off a full card stays plain. A full list and a passed
// cut-off both block joining; only the first is about capacity, so the badge
// has to say which.
export const fullCardBadgeOf = ({
  isBooked,
  hasEntry,
  gateReason,
}: {
  isBooked: boolean;
  hasEntry: boolean;
  gateReason: WaitlistGateReason | null;
}): FullCardBadge | null => {
  if (gateReason === 'NOT_FULL') return null;
  if (isBooked) return { label: 'Booked', color: 'green' };
  if (hasEntry) return { label: 'Waitlisted', color: 'yellow' };
  if (gateReason === 'DISABLED') return null;
  return gateReason === 'FULL'
    ? { label: 'Waitlist full', color: 'gray' }
    : { label: 'Class full', color: 'gray' };
};

// Her places only matter where a slot is full or already has a list: a live
// entry always counts itself, so a slot with neither cannot hold one.
export const hasWaitlistActivity = (availability: CalendarAvailability) =>
  Object.values(availability).some((slots) =>
    slots.some((slot) => isSlotFull(slot) || (slot.waitlistCount ?? 0) > 0)
  );

// A customer's places stay readable whatever stopped new activity: the
// platform pause (config null) or a studio waitlist that is off. Off is no
// proof the list is empty. Switching it off in Settings closes every place on
// save, but a plan that loses the waitlist leaves places open until the
// server's next sweep closes them, and she must still find her place to
// leave it. Only a studio not loaded yet skips the read.
export const canReadWaitlistEntries = (
  tenant: { waitlist_config?: WaitlistConfig | null } | undefined
): boolean => tenant?.waitlist_config !== undefined;

// An entry still holding a credit and a place in line. Its negation is every
// terminal status, so both sides stay in step as the union grows.
export const isLiveWaitlistEntry = (
  entry: Pick<WaitlistEntry, 'status'>
): boolean => entry.status === 'WAITING' || entry.status === 'OFFERED';

// The entry GET keeps saying OFFERED until the once-a-minute expiry sweep,
// but the deadline is already known: past it, a confirm can only fail.
export const hasOfferEnded = (
  entry: Pick<WaitlistEntry, 'status' | 'offer_expires_at'>,
  now = Date.now()
): boolean =>
  entry.status === 'OFFERED' &&
  !!entry.offer_expires_at &&
  new Date(entry.offer_expires_at).getTime() <= now;

export type WaitlistSlotKey = {
  slot_id: string | null;
  override_slot_id?: string | null;
  start_time: string | number;
};

// One live entry per customer per occurrence, so the match is unique.
export const findActiveEntryForSlot = (
  entries: LiveWaitlistEntry[] | undefined,
  slot: WaitlistSlotKey
): LiveWaitlistEntry | undefined => {
  if (!entries?.length) return undefined;
  const startMs = new Date(slot.start_time).getTime();
  return entries.find((entry) => {
    const sameSlot = slot.slot_id
      ? entry.occurrence.slot_id === slot.slot_id
      : entry.occurrence.override_slot_id === slot.override_slot_id;
    return (
      sameSlot && new Date(entry.occurrence.start_time).getTime() === startMs
    );
  });
};

export const classWithInstructorLine = (
  className: string,
  instructor: WaitlistEntry['instructor']
): string =>
  classWithInstructorText(
    className,
    instructor &&
      getAbbreviatedName(instructor.first_name, instructor.last_name)
  );

const CREDIT_NOT_RETURNED =
  'We could not return the credit we held. Contact us and we will help.';

const CREDIT_EXPIRED_NOT_RETURNED =
  'The credit we held expired while you were waiting, so it was not returned.';

// The one switch behind every "credit not returned" line: an expired source
// names itself, everything else keeps the contact-us fallback.
export const creditLostText = ({
  credit_lost_reason,
  hold,
}: {
  credit_lost_reason: CreditLostReason | null;
  hold?: WaitlistEntry['hold'];
}): string => {
  if (credit_lost_reason !== 'expired') return CREDIT_NOT_RETURNED;
  return hold?.expires_at
    ? `The credit we held from your ${holdSourceLabelOf(hold)} expired on ${formatDateWithConditionalYear(hold.expires_at)} while you were waiting, so it was not returned.`
    : CREDIT_EXPIRED_NOT_RETURNED;
};

// A live hold whose source dies before the waitlist closes: the sweep removes
// the entry the minute it expires, so a place that never got a spot loses it.
export const holdExpiryBeforeCutoff = (entry: {
  cutoff_at: string;
  hold: { expires_at: string | null };
}): string | null => {
  const expiresAt = entry.hold.expires_at;
  return expiresAt &&
    new Date(expiresAt).getTime() < new Date(entry.cutoff_at).getTime()
    ? expiresAt
    : null;
};

export type CreditExpiryRefusal = {
  sourceLabel: string | null;
  expiresAt: string | null;
};

// The source the join locked, which the lock-free preview may not have named.
// Validated, since `details` is whatever the server sent.
export const creditExpiryRefusalOf = (
  details: unknown
): CreditExpiryRefusal => {
  const fields = details as
    | { source_label?: unknown; expires_at?: unknown }
    | null
    | undefined;
  const sourceLabel = fields?.source_label;
  const expiresAt = fields?.expires_at;
  return {
    sourceLabel:
      typeof sourceLabel === 'string' && sourceLabel.trim()
        ? sourceLabel
        : null,
    expiresAt:
      typeof expiresAt === 'string' && !Number.isNaN(Date.parse(expiresAt))
        ? expiresAt
        : null,
  };
};

// The date is null only when the server refused a join without saying when
// the source expires; the warning still has to stand on its own.
export const creditExpiresBeforeClassText = (
  label: string,
  expiresAt: string | null
): string =>
  `The credit we hold from your ${label} expires${
    expiresAt ? ` on ${formatDateWithConditionalYear(expiresAt)}` : ''
  }, before this class. If no spot opens by then, it is lost.`;

export const CREDIT_EXPIRY_ACK_LABEL =
  'I understand the held credit is lost if no spot opens before it expires';

// One message for every unticked box, under the last of them.
export const joinCheckboxErrorOf = ({
  termsMissing,
  ackMissing,
}: {
  termsMissing: boolean;
  ackMissing: boolean;
}): string | null => {
  if (termsMissing && ackMissing) return 'Tick both boxes to join the waitlist';
  if (ackMissing) return 'Tick the box to accept that the credit may be lost';
  if (termsMissing) return 'Agree to the terms to join the waitlist';
  return null;
};

export const OFFER_EXPIRED_HEADER = {
  status: 'waitlisted',
  title: 'Your offer has expired',
} as const;

export const BOOKED_HEADER = {
  status: 'confirmed',
  title: "You're booked",
} as const;

// Past tense: her other place can close later, and this receipt stays.
export const DUPLICATE_PLACE_HEADER = {
  status: 'waitlisted',
  title: 'You already had a\nplace on this waitlist',
} as const;

export const CLAIM_MODE_NOTE = 'First to confirm gets the spot.';

// Plain nouns for "from your {label}" copy when the API has no display name.
const HOLD_SOURCE_FALLBACK: Record<WaitlistHoldSource, string> = {
  class_pack: 'class pack',
  subscription: 'membership',
  topup: 'top-up credits',
};

export const holdSourceLabelOf = (
  hold: Pick<WaitlistEntry['hold'], 'source_type' | 'source_label'>
): string => hold.source_label ?? HOLD_SOURCE_FALLBACK[hold.source_type];

// cutoff_hours can be 0, which would otherwise read "closes 0 hours before".
export const cutoffSentenceOf = (cutoffHours: number): string =>
  cutoffHours === 0
    ? 'The waitlist closes when the class starts.'
    : `The waitlist closes ${cutoffHours} ${
        cutoffHours === 1 ? 'hour' : 'hours'
      } before class starts.`;

// "Up to" in both: the server ends an offer at the cut-off, so one made near it
// is shorter than the window.
export const getModeSentence = (
  config: Pick<WaitlistConfig, 'mode' | 'offer_window_minutes'>
): string => {
  if (config.mode === 'AUTO_BOOK') {
    return 'If a spot opens, we book you in automatically and let you know.';
  }
  if (config.mode === 'CONFIRM') {
    return `If a spot opens, you get up to ${config.offer_window_minutes} minutes to confirm.`;
  }
  return 'If a spot opens, everyone on the list is notified. The first to confirm gets it.';
};

// A cutoff on the evening before an early class has to name its day, or
// "by 20:00" reads as the class day.
const cutoffLabelOf = (
  startTime: string,
  cutoffHours: number,
  timeFormat: TimeFormat,
  timeZone: string | undefined
): string => {
  const cutoff = new Date(cutoffAtOf(startTime, cutoffHours));
  return formatDateInTimeZone(cutoff, timeZone) ===
    formatDateInTimeZone(new Date(startTime), timeZone)
    ? formatClockTime(cutoff, timeFormat, timeZone)
    : formatClassDateTime(cutoff, timeFormat, timeZone, 'short');
};

export const getModeRow = (
  config: Pick<
    WaitlistConfig,
    'mode' | 'offer_window_minutes' | 'cutoff_hours'
  >,
  {
    startTime,
    timeFormat,
    timeZone,
  }: { startTime: string; timeFormat: TimeFormat; timeZone?: string }
): string => {
  const cutoff = cutoffLabelOf(
    startTime,
    config.cutoff_hours,
    timeFormat,
    timeZone
  );
  if (config.mode === 'AUTO_BOOK') {
    return `We book you in and email you if a spot opens by ${cutoff}`;
  }
  if (config.mode === 'CONFIRM') {
    return `If a spot opens by ${cutoff}, you get up to ${config.offer_window_minutes} minutes to confirm`;
  }
  return `If a spot opens by ${cutoff}, the first to confirm gets it`;
};

type CreditRowEntry = Pick<
  WaitlistEntry,
  | 'status'
  | 'closure_reason'
  | 'credit_returned'
  | 'credit_lost_reason'
  | 'hold'
  | 'cutoff_at'
>;

// A self_booked close with no credit back is the held credit paying for that
// booking, so it reads as used, like a booked entry. An offer past its deadline
// still holds the credit until the server's sweep closes the place.
export const creditRowCopyOf = (
  entry: CreditRowEntry,
  offerEnded = false
): { title: string; body: string } => {
  const holdLabel = holdSourceLabelOf(entry.hold);
  if (offerEnded && entry.status === 'OFFERED') {
    return {
      title: '1 credit on hold',
      body: `We're returning it to your ${holdLabel}`,
    };
  }
  if (isLiveWaitlistEntry(entry)) {
    const expiresAt = holdExpiryBeforeCutoff(entry);
    return {
      title: '1 credit on hold',
      body: expiresAt
        ? `It expires on ${formatDateWithConditionalYear(expiresAt)}, before this class: not returned if it expires while you wait.`
        : "Returned if you don't get a spot",
    };
  }
  if (
    entry.status === 'BOOKED' ||
    (entry.closure_reason === 'self_booked' && entry.credit_returned === null)
  ) {
    return { title: '1 credit used', body: `Booked with your ${holdLabel}` };
  }
  if (entry.credit_returned === false) {
    return {
      title:
        entry.credit_lost_reason === 'expired'
          ? 'Credit expired'
          : 'Credit not returned',
      body: creditLostText(entry),
    };
  }
  return { title: '1 credit returned', body: `Back on your ${holdLabel}` };
};

export type LeaveOutcome = {
  left: boolean;
  title: string;
  description?: string;
};

// Leaving answers 200 with the entry as it stands, so one that was already
// booked or closed comes back unchanged and must not read as a leave.
export const leaveOutcomeOf = (
  entry: Pick<
    WaitlistEntry,
    | 'status'
    | 'closure_reason'
    | 'credit_returned'
    | 'credit_lost_reason'
    | 'hold'
  >
): LeaveOutcome => {
  if (entry.status === 'BOOKED') {
    return { left: false, title: "You're already booked for this class" };
  }
  if (entry.closure_reason === 'duplicate') {
    return {
      left: false,
      title: DUPLICATE_PLACE_HEADER.title.replace('\n', ' '),
    };
  }
  if (entry.closure_reason !== 'left') {
    return { left: false, title: "You're no longer on this waitlist" };
  }
  return {
    left: true,
    title: 'You left the waitlist',
    description:
      entry.credit_returned === false
        ? creditLostText(entry)
        : `1 credit returned to your ${holdSourceLabelOf(entry.hold)}.`,
  };
};

export type NoCreditAction = { label: string; href: string };

export type NoCreditCopy = {
  title: string;
  description: string;
  actions: NoCreditAction[];
};

const NEEDS_CREDITS = {
  title: 'You need credits to join',
  description:
    "Joining the waitlist holds 1 credit from a pack, membership or top-up. You don't have one that covers this class yet.",
};

// No options means an older backend: the generic copy and the unfiltered pack
// list. The last action is the modal's primary button.
export const noCreditCopyOf = (
  options: WaitlistCreditOptions | undefined,
  classId: string
): NoCreditCopy => {
  if (!options) {
    return {
      ...NEEDS_CREDITS,
      actions: [{ label: 'See packs', href: OFFERING_LINKS.classPacks.href }],
    };
  }
  const buyActions: NoCreditAction[] = [
    ...(options.plans_available
      ? [
          {
            label: 'See plans',
            href: listCoveringClassPath('subscriptions', classId),
          },
        ]
      : []),
    ...(options.packs_available
      ? [
          {
            label: 'See packs',
            href: listCoveringClassPath('classPacks', classId),
          },
        ]
      : []),
  ];
  // Freeing a held credit costs nothing, so it is the primary action; buying
  // more stays on offer when a pack or plan covers this class.
  if (options.held_elsewhere) {
    return {
      title: 'Your credits are already held',
      description: buyActions.length
        ? 'Every credit that works for this class is held for another waitlist place. Leave one of those waitlists to free a credit, or buy more credits.'
        : 'Every credit that works for this class is held for another waitlist place. Leave one of those waitlists to free a credit.',
      actions: [
        ...buyActions,
        { label: 'See your waitlist places', href: '/dashboard' },
      ],
    };
  }
  if (!buyActions.length) {
    return {
      title: "You can't join this waitlist",
      description:
        'This class is not included in any pack or plan you can buy, so there is no credit to hold.',
      actions: [],
    };
  }
  return { ...NEEDS_CREDITS, actions: buyActions };
};
